import { useEffect, useRef, useState } from 'react'
import { Room, RoomEvent, Track } from 'livekit-client'
import { KrispNoiseFilter, isKrispNoiseFilterSupported } from '@livekit/krisp-noise-filter'
import { supabase } from '../lib/supabase'
import './VoiceWidget.css'

// The rev_user external_ref stores phones as +91XXXXXXXXXX, but the customers
// table stores bare 10-digit mobile_phone (e.g. 9740420699). Normalize to E.164
// (India) so the lookup matches. Leaves already-prefixed / non-10-digit as-is.
function toE164India(raw) {
  if (!raw) return ''
  const digits = String(raw).replace(/[^\d]/g, '')
  if (digits.length === 10) return `+91${digits}`
  if (digits.length === 12 && digits.startsWith('91')) return `+${digits}`
  return String(raw).startsWith('+') ? String(raw) : (digits ? `+${digits}` : '')
}

/**
 * ElevenLabs-style voice widget for YourInsurance.
 *
 * The orb places a REAL DevRev v1 voice call: POST /api/start-call
 * (handled by voice-proxy.mjs, PAT stays server-side) returns a LiveKit
 * { url, token }; we join the room and stream the agent's audio.
 *
 * Voice-only by design — text chat stays in the separate PLuG widget so we
 * don't imply a shared session that doesn't exist. Sits above the PLuG
 * launcher; nothing about the PLuG config changes.
 */
function VoiceWidget() {
  const [open, setOpen] = useState(false)
  const [call, setCall] = useState('idle') // idle | connecting | active
  const [status, setStatus] = useState('')
  const [statusKind, setStatusKind] = useState('')

  const roomRef = useRef(null)
  const audioElRef = useRef(null)
  const unmuteTimerRef = useRef(null)

  const say = (msg, kind = '') => { setStatus(msg); setStatusKind(kind) }

  // Resolve the logged-in customer's identity + context for the call.
  // Email/id come from localStorage; phone, name, and customer number are NOT
  // there, so we fetch them from the customers table. Phone is normalized to
  // +91 E.164 to match the rev_user external_ref. These are passed as the
  // meeting title + description so the voice agent has caller context.
  const getUserIdentity = async () => {
    let email = '', phone = '', id = ''
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}')
      const meta = u.user_metadata || {}
      email = u.email || u.user_email || meta.email || ''
      phone = u.phone || u.mobile || meta.phone || meta.mobile || ''
      id = u.id || ''
    } catch { /* ignore */ }

    let name = '', customerNumber = ''
    if (email || id) {
      try {
        const q = supabase
          .from('customers')
          .select('first_name,last_name,mobile_phone,customer_number')
        const { data } = await (id ? q.eq('customer_id', id) : q.eq('email', email)).single()
        if (data) {
          if (!phone) phone = data.mobile_phone
          name = [data.first_name, data.last_name].filter(Boolean).join(' ')
          customerNumber = data.customer_number || ''
        }
      } catch { /* row not found — email alone still resolves the rev_user */ }
    }

    return { email, phone: toE164India(phone), name, customerNumber }
  }

  // Friendly heads-up if .env.voice is incomplete.
  useEffect(() => {
    fetch('/api/voice-config')
      .then((r) => r.json())
      .then((cfg) => { if (!cfg.ready) say(`Set ${cfg.missing.join(', ')} in .env.voice`, 'error') })
      .catch(() => {})
  }, [])

  async function startCall() {
    setCall('connecting')
    say('Connecting…')

    let creds
    let identity = {}
    try {
      identity = await getUserIdentity()
      // eslint-disable-next-line no-console
      console.log('[VoiceWidget] sending identity →', identity)
      const res = await fetch('/api/start-call', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(identity),
      })
      creds = await res.json()
      if (!res.ok) throw new Error(creds.error || `HTTP ${res.status}`)
      // eslint-disable-next-line no-console
      console.log('[VoiceWidget] identified as logged-in customer:', creds.identified)
    } catch (e) {
      setCall('idle')
      say(e.message || 'Could not start the call.', 'error')
      return
    }

    try {
      const room = new Room({
        adaptiveStream: true,
        dynacast: true,
        // Let the BROWSER do echo cancellation + gain, but turn OFF its native
        // noiseSuppression — Krisp owns noise below. Running both fights and
        // produces choppy/robotic audio. voiceIsolation off for the same reason.
        audioCaptureDefaults: {
          echoCancellation: true,
          autoGainControl: true,
          noiseSuppression: false,
          voiceIsolation: false,
        },
      })
      roomRef.current = room

      room.on(RoomEvent.TrackSubscribed, (track) => {
        if (track.kind === 'audio') {
          const el = track.attach()
          el.autoplay = true
          document.body.appendChild(el)
          audioElRef.current = el
        }
      })
      room.on(RoomEvent.ParticipantConnected, () => say('Agent connected — say hello 👋'))
      room.on(RoomEvent.Disconnected, () => cleanup(true))

      // Half-duplex echo guard (for demos on a loud external speaker): while the
      // AGENT is speaking, mute the customer mic so the room audio can't loop
      // back into the mic and trip the agent's turn-detection (making it cut out).
      // A short unmute delay after the agent stops swallows the speaker's tail.
      // Default ON; disable with ?fullduplex=1 (e.g. when using headphones).
      const halfDuplex = new URLSearchParams(window.location.search).get('fullduplex') !== '1'
      if (halfDuplex) {
        room.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
          const lp = roomRef.current?.localParticipant
          if (!lp) return
          const agentSpeaking = speakers.some((s) => s.identity !== lp.identity)
          if (agentSpeaking) {
            if (unmuteTimerRef.current) { clearTimeout(unmuteTimerRef.current); unmuteTimerRef.current = null }
            if (!lp.isMicrophoneEnabled) return
            lp.setMicrophoneEnabled(false).catch(() => {})
            say('Agent speaking…')
          } else {
            // brief delay so the speaker's trailing audio doesn't re-trigger
            if (unmuteTimerRef.current) clearTimeout(unmuteTimerRef.current)
            unmuteTimerRef.current = setTimeout(() => {
              const p = roomRef.current?.localParticipant
              if (p && !p.isMicrophoneEnabled) p.setMicrophoneEnabled(true).catch(() => {})
              if (roomRef.current) say('Listening…')
            }, 400)
          }
        })
      }

      await room.connect(creds.url, creds.token)

      // The LiveKit identity (DON) is minted for the API caller (devu/1) and
      // can't be changed client-side — but we can set a human-readable name and
      // attributes on our participant. If the agent reads participant name/attrs
      // (rather than the raw DON), this is how it learns the real caller.
      try {
        const lp = room.localParticipant
        if (identity.name && lp.setName) await lp.setName(identity.name)
        if (lp.setAttributes) {
          await lp.setAttributes({
            customer_name: identity.name || '',
            customer_email: identity.email || '',
            customer_phone: identity.phone || '',
            customer_number: identity.customerNumber || '',
          })
        }
      } catch (_) { /* older SDK without setName/setAttributes — non-fatal */ }

      await room.localParticipant.setMicrophoneEnabled(true) // asks for mic

      // Krisp noise cancellation on the customer's mic (LiveKit Cloud only).
      //   quality:'medium' → Krisp's recommended default; lighter than 'high',
      //                      which can overload weaker devices and cause choppy
      //                      audio. Bump to 'high' only if a device proves capable.
      //   useBVC:true      → Background Voice Cancellation (strips OTHER people's
      //                      voices). NOTE: device-gated — silently falls back to
      //                      plain NC if the mic isn't BVC-compatible or <16kHz.
      //   debugLogs/onBufferDrop → telemetry: buffer drops == CPU overload (the
      //                      real cause of choppiness), distinct from BVC not firing.
      // A/B knobs via URL param so we can diagnose on a LIVE call without a
      // redeploy. "breakage when external noise comes" == audio gating/dropout:
      //   ?nc=off    → no Krisp at all (isolates the browser's own AGC/gating)
      //   ?nc=nobvc  → Krisp WITHOUT BVC (BVC over-gates loud noise → dropouts)
      //   ?nc=low|medium|high → model weight (tests CPU-overload theory)
      // default (no param) = medium + BVC.
      const nc = new URLSearchParams(window.location.search).get('nc') || 'default'
      try {
        if (nc === 'off') {
          // eslint-disable-next-line no-console
          console.log('[VoiceWidget] NC OFF (?nc=off) — raw mic, browser AGC only. Use this to check if Krisp is the one gating on noise.')
        } else if (isKrispNoiseFilterSupported()) {
          const micPub = room.localParticipant.getTrackPublication(Track.Source.Microphone)
          if (micPub?.track) {
            const quality = ['low', 'medium', 'high'].includes(nc) ? nc : 'medium'
            const useBVC = nc !== 'nobvc'
            let bufferDrops = 0
            const processor = KrispNoiseFilter({
              quality,
              useBVC,
              // debugLogs shows Krisp's "in debug mode" banner + Download Log
              // Report UI. Off by default; opt in with ?debug in the URL.
              debugLogs: new URLSearchParams(window.location.search).has('debug'),
              onBufferDrop: () => {
                bufferDrops += 1
                // eslint-disable-next-line no-console
                console.warn(`[VoiceWidget] Krisp buffer drop #${bufferDrops} — CPU overload; audio stutters. Try ?nc=low.`)
              },
            })
            await micPub.track.setProcessor(processor)
            // eslint-disable-next-line no-console
            console.log(`[VoiceWidget] Krisp enabled — quality:${quality}, BVC:${useBVC}. If audio breaks up on external noise: try ?nc=nobvc (BVC over-gating) or ?nc=off. Watch for "buffer drop" warnings (=CPU).`)
          }
        } else {
          // eslint-disable-next-line no-console
          console.log('[VoiceWidget] Krisp not supported in this browser — using raw mic')
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[VoiceWidget] Krisp noise filter failed, continuing without it:', err)
      }

      setCall('active')
      say('Listening…')
    } catch (e) {
      setCall('idle')
      say(e?.name === 'NotAllowedError' ? 'Microphone permission is required.' : (e.message || 'Failed to join.'), 'error')
      cleanup()
    }
  }

  async function cleanup(fromEvent = false) {
    if (unmuteTimerRef.current) { clearTimeout(unmuteTimerRef.current); unmuteTimerRef.current = null }
    const room = roomRef.current
    if (room && !fromEvent) { try { await room.disconnect() } catch (_) {} }
    roomRef.current = null
    if (audioElRef.current) { try { audioElRef.current.remove() } catch (_) {} audioElRef.current = null }
    setCall('idle')
    say('Call ended.')
    setTimeout(() => setStatus((s) => (s === 'Call ended.' ? '' : s)), 2500)
  }

  const onOrbClick = () => {
    if (call === 'active') cleanup()
    else if (call !== 'connecting') startCall()
  }

  return (
    <div className="viw" data-open={open} data-call={call}>
      <section className="viw-card" role="dialog" aria-label="Voice assistant" aria-hidden={!open}>
        <div className="viw-top">
          <span className="viw-brand-tag">YourInsurance</span>
          <button className="viw-collapse" type="button" aria-label="Minimize" onClick={() => setOpen(false)}>
            <svg viewBox="0 0 24 24" width="16" height="16"><path d="M9 15l-5 5m0 0h4m-4 0v-4M15 9l5-5m0 0h-4m4 0v4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
        </div>

        <div className="viw-orb-wrap">
          <button className="viw-orb" type="button" aria-label={call === 'active' ? 'End call' : 'Start voice call'} onClick={onOrbClick}>
            <span className="viw-orb-ring viw-orb-grad" aria-hidden="true" />
            <span className="viw-orb-core">
              <svg className="viw-ic viw-ic-phone" viewBox="0 0 24 24" width="26" height="26"><path d="M6.6 10.8a15.9 15.9 0 0 0 6.6 6.6l2.2-2.2a1 1 0 0 1 1-.24c1.1.37 2.3.57 3.5.57a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.2.2 2.4.57 3.5a1 1 0 0 1-.24 1L6.6 10.8z" fill="currentColor"/></svg>
              <svg className="viw-ic viw-ic-hangup" viewBox="0 0 24 24" width="26" height="26"><path d="M12 9c-2 0-3.9.3-5.6.9-.5.2-.8.6-.8 1.1v2.1c0 .4.2.7.6.9l2.3 1c.4.2.9 0 1.1-.4l.7-1.6c.1-.3.4-.5.7-.5h2c.3 0 .6.2.7.5l.7 1.6c.2.4.7.6 1.1.4l2.3-1c.4-.2.6-.5.6-.9V11c0-.5-.3-.9-.8-1.1C15.9 9.3 14 9 12 9z" fill="currentColor" transform="rotate(135 12 12)"/></svg>
            </span>
          </button>
        </div>

        <p className="viw-caption">Talk to the YourInsurance voice assistant about claims, policies, and support.</p>
        <p className="viw-status" data-kind={statusKind} aria-live="polite">{status}</p>
      </section>

      <button className="viw-launcher" type="button" aria-label="Open voice assistant" onClick={() => setOpen(true)}>
        <span className="viw-launcher-orb viw-orb-grad" aria-hidden="true" />
        <span className="viw-launcher-label">Voice chat</span>
      </button>
    </div>
  )
}

export default VoiceWidget
